// Single source of truth for the mortgage-refinance progress bar.
//
// The flow spans two top-level Vue components (MortgageRefinanceFlow for the
// signup phase, MortgageRefinanceConnect for the bank-connection phase) with
// an Auth0 2FA redirect in between. We want one continuous progress bar
// across the whole journey, so both components map their current local step
// id into a position in this shared sequence.
//
// Branch / error states (mockBankCreds, missingMortgage, missingBalance,
// error, genericError) are not part of the linear happy-path bar — they
// map to the position of the step the user came from so the bar never
// moves backwards.

const SIGNUP_STEPS = ['task', 'priority', 'name', 'email', 'phone', 'otp'];

// Happy-path bank steps that advance the bar.
const BANK_HAPPY_STEPS = ['bankIntro', 'bankSelect', 'bankCreds', 'bankLoading'];

// All steps that map to a bar position. Branch/error states reuse the
// position of the step that triggered them.
const ALL_STEPS = [...SIGNUP_STEPS, ...BANK_HAPPY_STEPS];

// Branch state → position alias. The progress bar should sit at the position
// of whatever step the user was on when they landed in the branch.
const BRANCH_ALIASES = {
  mockBankCreds: 'bankCreds', // mock variant of bank creds
  missingMortgage: 'bankLoading', // post-loading dead-end
  missingBalance: 'bankLoading', // post-loading dead-end
  error: 'bankCreds', // connection error after creds
  // genericError fires from restoreFromBackendState, the mortgage-data
  // timeout, or handleAsyncFallback — all happen during/after bankLoading.
  // FlowHeader hides the progress bar via :hide-progress="isErrorStep",
  // but we alias defensively so stepPosition('genericError') never falls
  // through to step 1 if a future caller uses it.
  genericError: 'bankLoading',
};

/**
 * Returns the 1-indexed position of a step in the combined flow.
 * Unknown steps return 1.
 */
function stepPosition(stepId) {
  const resolved = BRANCH_ALIASES[stepId] || stepId;
  const idx = ALL_STEPS.indexOf(resolved);
  return idx >= 0 ? idx + 1 : 1;
}

const TOTAL_STEPS = ALL_STEPS.length;

// Sentinel value sent to the Open Banking backend as `otsFlowId` and read
// back on the OBK landing page to route the customer into the mortgage
// flow instead of the OTS one. Backend currently reuses the otsFlowId
// param for both — keep this constant as the single source of truth so
// the producer (MortgageRefinanceConnect) and consumer
// (OpenBankingLandingPage) can never drift.
const MORTGAGE_FLOW_ID = 'mortgage-refinance';

module.exports = {
  SIGNUP_STEPS,
  BANK_HAPPY_STEPS,
  ALL_STEPS,
  TOTAL_STEPS,
  MORTGAGE_FLOW_ID,
  stepPosition,
};
